import type { AudioSource } from "@-/com.npmjs.expo-audio";
import type { VoiceStudyPrompt } from "#voice-study-card";

export type VoiceStudyStatus =
  | "idle"
  | "speaking"
  | "listening"
  | "processing"
  | "paused"
  | "error";

export type VoiceStudyAudio = {
  /** Start one native capture; `onEnd` fires when it stops on its own (silence, limits). */
  listen: (
    onResult: (text: string) => void,
    onEnd: () => void,
    onError: (error: Error, transient: boolean) => void,
  ) => void;
  playClip: (source: AudioSource) => Promise<void>;
  speak: (text: string) => Promise<void>;
  stopListening: () => Promise<void>;
  /** Stops the clip and the utterance; their promises resolve. */
  stopSpeaking: () => Promise<void>;
};

export type VoiceStudySessionTurn = {
  id: string;
  /** Return a prompt to speak as feedback (listening continues); void means navigation owns the next turn. */
  onTranscript?: (text: string) => Promise<VoiceStudyPrompt | void>;
  prompt: VoiceStudyPrompt;
};

/** One line of the voice transcript: what the app said, or what was heard (or typed). */
export type VoiceStudySessionMessage = {
  /** A heard transcript that was dropped (echo during a prompt, or a handler was busy). */
  ignored?: boolean;
  role: "app" | "user";
  text: string;
};

const RESTART_DELAY_MS = 350;
const RETRY_DELAY_MS = 150;
const MAX_RETRIES = 5;

/**
 * One owner of the microphone and speaker across front/back navigation.
 *
 * The microphone runs continuously from `start()` to `pause()`/`stop()` — including
 * while a prompt is being spoken, so "next" can interrupt a long back reading. A
 * transcript heard mid-speech is only honoured when `isBargeIn` says so; everything
 * else captured during speech is treated as echo and dropped. Native recognizers
 * end on silence and time limits; every end restarts capture, and transient errors
 * (interruptions, route changes while locked) retry a few times before the
 * microphone is given up. A dead microphone does not end the session: prompts keep
 * playing and `hear()` (the emulated-speech input) keeps working.
 */
export class VoiceStudySession {
  private generation = 0;
  private active = false;
  private listening = false;
  private capturing = false;
  private starting = false;
  private speaking = false;
  private busy = false;
  private muted = false;
  private retries = 0;
  private turn: VoiceStudySessionTurn | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private pendingSpeechStop = Promise.resolve();
  private pendingMicrophoneStop = Promise.resolve();

  constructor(
    private readonly audio: VoiceStudyAudio,
    private readonly onStatus: (status: VoiceStudyStatus, error?: Error) => void,
    private readonly options: {
      isBargeIn: (text: string) => boolean;
      onMessage?: (message: VoiceStudySessionMessage) => void;
    },
  ) {}

  private fail(error: Error): void {
    this.stop();
    this.onStatus("error", error);
  }

  /** The microphone is gone but the session continues: prompts still play, typed input still works. */
  private loseMicrophone(error: Error): void {
    this.listening = false;
    clearTimeout(this.timer);
    this.onStatus("error", error);
  }

  private reportIdle(): void {
    if (!this.active) {
      return;
    }
    this.onStatus(this.listening && this.turn?.onTranscript ? "listening" : "idle");
  }

  private interruptSpeech(): { generation: number; stopped: Promise<void> } {
    const generation = ++this.generation;
    this.speaking = false;
    const stop = this.audio.stopSpeaking();
    this.pendingSpeechStop = Promise.all([this.pendingSpeechStop.catch(() => {}), stop]).then(
      () => {},
    );
    return { generation, stopped: this.pendingSpeechStop };
  }

  private listen(): void {
    if (!this.listening || this.starting) {
      return;
    }
    this.starting = true;
    // Native capture must have confirmed its end before another start — the LATEST end:
    // a pause issued while this start was waiting re-points the pending stop.
    const awaitMicrophoneStop = (): void => {
      const pending = this.pendingMicrophoneStop;
      void pending
        .catch(() => {})
        .then(() => {
          if (this.pendingMicrophoneStop !== pending) {
            awaitMicrophoneStop();
            return;
          }
          this.starting = false;
          if (!this.listening || this.capturing) {
            return;
          }
          this.capturing = true;
          this.audio.listen(
            (text) => {
              this.retries = 0;
              this.route(text);
            },
            () => {
              this.capturing = false;
              if (this.listening) {
                this.timer = setTimeout(() => this.listen(), RESTART_DELAY_MS);
              }
            },
            (error, transient) => {
              this.capturing = false;
              if (!this.listening) {
                return;
              }
              if (transient && this.retries < MAX_RETRIES) {
                this.retries += 1;
                this.timer = setTimeout(() => this.listen(), RETRY_DELAY_MS);
                return;
              }
              this.loseMicrophone(error);
            },
          );
        });
    };
    awaitMicrophoneStop();
  }

  private route(text: string): void {
    const trimmed = text.trim();
    if (!this.active || trimmed.length === 0) {
      return;
    }
    const turn = this.turn;
    const ignored =
      this.busy ||
      !turn?.onTranscript ||
      (this.speaking && (turn.prompt.bargeIn === false || !this.options.isBargeIn(trimmed)));
    this.options.onMessage?.({ ignored: ignored || undefined, role: "user", text: trimmed });
    if (ignored || !turn?.onTranscript) {
      return;
    }
    if (this.speaking) {
      this.interruptSpeech();
    }
    this.busy = true;
    this.onStatus("processing");
    void turn
      .onTranscript(trimmed)
      .then((feedback) => {
        this.busy = false;
        if (!this.active || this.turn !== turn) {
          return;
        }
        if (feedback !== undefined) {
          this.run({ ...turn, prompt: feedback }, this.muted);
        } else if (!this.speaking) {
          this.reportIdle();
        }
      })
      .catch((error: Error) => {
        this.busy = false;
        this.fail(error);
      });
  }

  private async play(prompt: VoiceStudyPrompt, generation: number): Promise<void> {
    const current = () => generation === this.generation;
    this.speaking = true;
    this.onStatus("speaking");
    if (prompt.clip !== null && prompt.clip !== undefined) {
      await this.audio.playClip(prompt.clip);
    }
    if (current() && prompt.text.trim().length > 0) {
      await this.audio.speak(prompt.text);
    }
    if (current()) {
      this.speaking = false;
    }
  }

  private announce(prompt: VoiceStudyPrompt): void {
    if (prompt.text.trim().length > 0) {
      this.options.onMessage?.({ role: "app", text: prompt.text.trim() });
    }
  }

  /** Begin (or resume) the session and continuous listening. Idempotent. */
  start(): void {
    if (this.active) {
      return;
    }
    this.active = true;
    this.listening = true;
    this.retries = 0;
    this.listen();
    this.reportIdle();
  }

  /** Stop speaking and listening; the turn is kept so `resume()` can replay it. */
  pause(): void {
    this.suspend();
    this.onStatus("paused");
  }

  resume(): void {
    this.start();
  }

  stop(): void {
    this.suspend();
    this.turn = null;
    this.busy = false;
    this.onStatus("idle");
  }

  private suspend(): void {
    this.active = false;
    this.listening = false;
    clearTimeout(this.timer);
    const { generation, stopped } = this.interruptSpeech();
    const microphoneStop = this.audio.stopListening();
    this.pendingMicrophoneStop = Promise.all([
      this.pendingMicrophoneStop.catch(() => {}),
      microphoneStop,
    ]).then(() => {
      // The adapter suppresses `onEnd` for a capture we aborted; release the slot here.
      this.capturing = false;
    });
    void Promise.all([stopped, microphoneStop]).catch((error: Error) => {
      if (generation === this.generation) {
        this.onStatus("error", error);
      }
    });
  }

  /** Feed a transcript as if the recognizer heard it (the emulated-speech input box). */
  hear(text: string): void {
    this.route(text);
  }

  /** Make `turn` current and speak its prompt (unless muted); listening continues throughout. */
  run(turn: VoiceStudySessionTurn | null, muted: boolean): void {
    this.turn = turn;
    this.muted = muted;
    const { generation, stopped } = this.interruptSpeech();
    const current = () => generation === this.generation;
    if (turn !== null && this.active) {
      this.announce(turn.prompt);
    }
    void stopped
      .then(async () => {
        if (turn === null || !current()) {
          return;
        }
        if (!muted && this.active) {
          await this.play(turn.prompt, generation);
        }
        if (current()) {
          this.reportIdle();
        }
      })
      .catch((error: Error) => {
        if (current()) {
          this.fail(error);
        }
      });
  }

  /** Speak once without changing the current turn; resolves when done or interrupted. */
  async say(prompt: VoiceStudyPrompt): Promise<void> {
    const { generation, stopped } = this.interruptSpeech();
    await stopped;
    if (generation !== this.generation || !this.active) {
      return;
    }
    this.announce(prompt);
    if (this.muted) {
      return;
    }
    try {
      await this.play(prompt, generation);
    } finally {
      if (generation === this.generation) {
        this.reportIdle();
      }
    }
  }
}
